/**
 * Data Flow Animation
 * Animates particles flowing along connection paths
 */

import { Coords, Connector } from 'src/types';
import { AnimationEngine } from './AnimationEngine';
import { DataFlowConfig } from './animationTypes';
import { generateUniqueId, lerp } from './utils';
// import { generateUniqueId, lerp } from './utils';

interface DataFlowParticle {
    id: string;
    position: number; // 0-1 along the path
    speed: number;
    size: number;
    color: string;
    opacity: number;
}

export class DataFlowAnimation {
    private engine: AnimationEngine;
    private particles: Map<string, DataFlowParticle[]> = new Map();
    private pathCache: Map<string, Coords[]> = new Map();

    constructor(engine: AnimationEngine) {
        this.engine = engine;
    }

    /**
     * Start a data flow animation on a connector
     */
    public startDataFlow(
        connector: Connector,
        path: Coords[],
        config: DataFlowConfig = {}
    ): string {
        const {
            duration = 3000,
            speed = 1,
            particleCount = 5,
            particleSize = 8,
            particleColor = '#3b82f6',
            loop = true,
            direction = 'forward'
        } = config;

        const animationId = generateUniqueId('dataflow');

        // Cache the path for this connector
        this.pathCache.set(connector.id, path);

        // Create particles
        const particles: DataFlowParticle[] = [];
        for (let i = 0; i < particleCount; i++) {
            particles.push({
                id: generateUniqueId('particle'),
                position: direction === 'backward' ? 1 : 0,
                speed: speed * (0.8 + Math.random() * 0.4), // Add some variation
                size: particleSize,
                color: particleColor,
                opacity: 1
            });
        }

        this.particles.set(connector.id, particles);

        // Create a dummy target for the animation
        const animationTarget = {
            id: animationId,
            progress: 0
        };

        // Animate the progress value
        const id = this.engine.animate(
            animationTarget,
            { progress: 1 },
            {
                duration,
                repeat: loop ? -1 : 0,
                easing: 'linear',
                onUpdate: (progress) => {
                    this.updateParticles(connector.id, progress, direction);
                },
                onComplete: () => {
                    this.particles.delete(connector.id);
                    this.pathCache.delete(connector.id);
                },
                onStop: () => {
                    this.particles.delete(connector.id);
                    this.pathCache.delete(connector.id);
                }
            }
        );

        return id;
    }

    /**
     * Update particle positions
     */
    private updateParticles(
        connectorId: string,
        animationProgress: number,
        direction: 'forward' | 'backward' | 'bidirectional'
    ): void {
        const particles = this.particles.get(connectorId);
        if (!particles) return;

        particles.forEach((particle, index) => {
            // Stagger particles along the path
            const stagger = index / particles.length;
            let position = (animationProgress + stagger) % 1;

            if (direction === 'backward') {
                position = 1 - position;
            } else if (direction === 'bidirectional') {
                // Particles go forward then backward
                if (animationProgress < 0.5) {
                    position = position * 2;
                } else {
                    position = 2 - position * 2;
                }
            }

            particle.position = position;

            // Fade in/out at edges
            const fadeDistance = 0.1;
            if (position < fadeDistance) {
                particle.opacity = position / fadeDistance;
            } else if (position > 1 - fadeDistance) {
                particle.opacity = (1 - position) / fadeDistance;
            } else {
                particle.opacity = 1;
            }
        });
    }

    /**
     * Get particles for a connector
     */
    public getParticles(connectorId: string): DataFlowParticle[] {
        return this.particles.get(connectorId) || [];
    }

    /**
     * Get the interpolated position of a particle on the path
     */
    public getParticlePosition(
        connectorId: string,
        particle: DataFlowParticle
    ): Coords | null {
        const path = this.pathCache.get(connectorId);
        if (!path || path.length < 2) return null;

        // Calculate which segment the particle is on
        const totalSegments = path.length - 1;
        const segmentProgress = particle.position * totalSegments;
        const segmentIndex = Math.floor(segmentProgress);
        const segmentT = segmentProgress - segmentIndex;

        // Handle edge case where particle is at the end
        if (segmentIndex >= totalSegments) {
            return path[path.length - 1];
        }

        // Interpolate between two points
        const from = path[segmentIndex];
        const to = path[segmentIndex + 1];

        return {
            x: lerp(from.x, to.x, segmentT),
            y: lerp(from.y, to.y, segmentT)
        };
    }

    /**
     * Stop all data flows
     */
    public stopAll(): void {
        this.particles.clear();
        this.pathCache.clear();
    }
}

/**
 * Create a standalone data flow animation
 */
export function createDataFlowAnimation(
    engine: AnimationEngine,
    connector: Connector,
    path: Coords[],
    config?: DataFlowConfig
): { animationId: string; controller: DataFlowAnimation } {
    const controller = new DataFlowAnimation(engine);
    const animationId = controller.startDataFlow(connector, path, config);

    return { animationId, controller };
}